import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { FaEye } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { baseurl } from "../../config/url";

const PAGE_SIZE = 12;

const safeText = (v) => (v === null || v === undefined || v === "" ? "-" : String(v));
const lower = (v) => safeText(v).toLowerCase();

const inr = (v) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return "-";
  return new Intl.NumberFormat("en-IN", { maximumFractionDigits: 2 }).format(n);
};

const dtIN = (d) => {
  if (!d) return "-";
  const x = new Date(d);
  if (Number.isNaN(x.getTime())) return "-";
  return x.toLocaleString("en-IN");
};

// token support (adminToken OR token OR auth.token)
const getToken = () =>
  localStorage.getItem("adminToken") ||
  localStorage.getItem("token") ||
  (() => {
    try {
      return JSON.parse(localStorage.getItem("auth") || "{}")?.token || "";
    } catch {
      return "";
    }
  })();

const pickCategory = (p) => p?.category?.name || p?.categoryName || p?.category || "-";
const pickSellerName = (p) =>
  p?.seller?.companyName ||
  p?.seller?.name ||
  p?.sellerName ||
  p?.createdBy?.name ||
  p?.userId?.name ||
  "-";

const NewProduct = () => {
  const navigate = useNavigate();

  const [products, setProducts] = useState([]);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // ✅ Fetch real products
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);
        setError("");

        const token = getToken();

        const { data } = await axios.get(`${baseurl}/api/product`, {
          headers: token ? { Authorization: `Bearer ${token}` } : undefined,
          withCredentials: true,
        });

        const list = Array.isArray(data)
          ? data
          : Array.isArray(data?.products)
          ? data.products
          : [];

        // ✅ recently added = sort by createdAt desc
        list.sort((a, b) => new Date(b?.createdAt || 0) - new Date(a?.createdAt || 0));

        setProducts(list);
      } catch (err) {
        console.error("Error fetching products:", err.response?.data || err.message);
        setError(err.response?.data?.message || "Failed to load products");
        setProducts([]);
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, []);

  // ✅ Filter
  const filtered = useMemo(() => {
    const q = lower(search.trim());
    if (!q) return products;

    return products.filter((p) => {
      return (
        lower(p?._id).includes(q) ||
        lower(p?.name).includes(q) ||
        lower(pickCategory(p)).includes(q) ||
        lower(pickSellerName(p)).includes(q) ||
        lower(p?.saleType).includes(q)
      );
    });
  }, [products, search]);

  // reset page on search
  useEffect(() => setPage(1), [search]);

  // ✅ Pagination
  const totalItems = filtered.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / PAGE_SIZE));
  const start = (page - 1) * PAGE_SIZE;
  const pageItems = filtered.slice(start, start + PAGE_SIZE);

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const openDetails = (id) => {
    if (!id) return;
    navigate(`/products/details/${id}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
        <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-5">
          <div className="text-lg font-semibold text-gray-800">Loading recently added products…</div>
          <div className="text-sm text-gray-500 mt-1">Please wait</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4 sm:p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 flex-wrap mb-5">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Recently Added Products</h1>
            <p className="text-sm text-gray-600 mt-1">
              Latest products (sorted by createdAt). Search and open details.
            </p>
          </div>

          <div className="text-sm text-gray-600 bg-white border border-gray-200 rounded-lg px-3 py-2">
            Total: <b>{products.length}</b> • Showing: <b>{filtered.length}</b>
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm">
            {error}
          </div>
        )}

        {/* Search */}
        <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-4 mb-5">
          <label className="text-xs text-gray-500">Search</label>
          <input
            type="text"
            placeholder="Search by Product / Category / Seller / ID / Sale Type..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full mt-1 px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400"
          />
        </div>

        {/* Table */}
        <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-100 text-gray-700">
              <tr>
                <th className="px-4 py-3 text-left font-semibold">#</th>
                <th className="px-4 py-3 text-left font-semibold">Product</th>
                <th className="px-4 py-3 text-left font-semibold">Category</th>
                <th className="px-4 py-3 text-left font-semibold">Seller</th>
                <th className="px-4 py-3 text-left font-semibold">Price</th>
                <th className="px-4 py-3 text-left font-semibold">Stock</th>
                <th className="px-4 py-3 text-left font-semibold">Created</th>
                <th className="px-4 py-3 text-right font-semibold">Action</th>
              </tr>
            </thead>

            <tbody>
              {pageItems.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-4 py-10 text-center text-gray-500">
                    No products found.
                  </td>
                </tr>
              ) : (
                pageItems.map((p, idx) => (
                  <tr key={p?._id || idx} className="border-t hover:bg-gray-50">
                    <td className="px-4 py-3 text-gray-700">{start + idx + 1}</td>

                    <td className="px-4 py-3">
                      <div className="font-semibold text-gray-900">{safeText(p?.name)}</div>
                      <div className="text-xs text-gray-500 font-mono break-all">{safeText(p?._id)}</div>
                      <div className="text-xs text-gray-500 mt-1">
                        Sale Type: <span className="font-semibold text-gray-700">{safeText(p?.saleType)}</span>
                      </div>
                    </td>

                    <td className="px-4 py-3 text-gray-700">{safeText(pickCategory(p))}</td>
                    <td className="px-4 py-3 text-gray-700">{safeText(pickSellerName(p))}</td>

                    <td className="px-4 py-3 text-gray-700">
                      ₹{inr(p?.price)}{" "}
                      <span className="text-xs text-gray-500">
                        {p?.priceUnit ? `(${p.priceUnit})` : ""}
                      </span>
                    </td>

                    <td className="px-4 py-3 text-gray-700">{safeText(p?.stock)}</td>
                    <td className="px-4 py-3 text-gray-700">{dtIN(p?.createdAt)}</td>

                    <td className="px-4 py-3">
                      <div className="flex justify-end">
                        <button
                          onClick={() => openDetails(p?._id)}
                          className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700"
                          title="View details"
                        >
                          <FaEye />
                          View
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>

          {/* Pagination */}
          {totalItems > 0 && (
            <div className="flex items-center justify-between gap-3 flex-wrap p-4 border-t border-gray-200">
              <div className="text-sm text-gray-600">
                Page <b>{page}</b> of <b>{totalPages}</b> • Showing{" "}
                <b>{start + 1}</b>–<b>{Math.min(start + PAGE_SIZE, totalItems)}</b> of{" "}
                <b>{totalItems}</b>
              </div>

              <div className="flex items-center gap-2 flex-wrap">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="px-3 py-2 bg-gray-100 rounded-lg hover:bg-gray-200 disabled:opacity-50"
                >
                  Prev
                </button>

                {(() => {
                  const maxBtns = 7;
                  let s = Math.max(1, page - Math.floor(maxBtns / 2));
                  let e = Math.min(totalPages, s + maxBtns - 1);
                  s = Math.max(1, e - maxBtns + 1);

                  const btns = [];
                  for (let p = s; p <= e; p++) btns.push(p);

                  return btns.map((p) => (
                    <button
                      key={p}
                      onClick={() => setPage(p)}
                      className={`px-3 py-2 rounded-lg border ${
                        p === page
                          ? "bg-blue-600 text-white border-blue-600"
                          : "bg-white text-blue-600 border-blue-200 hover:bg-blue-50"
                      }`}
                    >
                      {p}
                    </button>
                  ));
                })()}

                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="px-3 py-2 bg-gray-100 rounded-lg hover:bg-gray-200 disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
{/* 
        <p className="text-xs text-gray-500 mt-3">
          View opens your route: <b>/products/details/:id</b>
        </p> */}
      </div>
    </div>
  );
};

export default NewProduct;